import { useState, useEffect } from "react";
import { X, Split, Boxes, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

export function ClientRoutePrompt({ client, onYes, onNo }) {
  const { t } = useI18n();
  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 p-4" data-testid="client-route-prompt">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-sm shadow-2xl p-6">
        <h3 className="font-heading font-bold text-lg mb-2 flex items-center gap-2">
          <Split size={18} className="text-amber-400" /> {t("Wyznaczyć trasę kabla?")}
        </h3>
        <p className="text-sm text-slate-300 mb-5">
          {t("Klient")} <b className="text-slate-100">{client?.name}</b> {t("został zapisany. Czy chcesz teraz wyznaczyć trasę kabla klienckiego od abonenta do splittera?")}
        </p>
        <div className="flex gap-2 justify-end">
          <button onClick={onNo} data-testid="client-route-no" className="px-4 py-2 rounded text-sm bg-slate-700 hover:bg-slate-600">{t("Nie teraz")}</button>
          <button onClick={onYes} data-testid="client-route-yes" className="px-4 py-2 rounded text-sm bg-amber-600 hover:bg-amber-500 text-white font-medium">{t("Tak, wyznacz trasę")}</button>
        </div>
      </div>
    </div>
  );
}

export function SplitterConnectDialog({ connect, clientName, onDecline, onConnect }) {
  const { t } = useI18n();
  const [confirmed, setConfirmed] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const splitter = connect?.splitter;
  const closure = connect?.closure;

  useEffect(() => {
    if (!confirmed || !splitter) return;
    setLoading(true);
    api.get(`/splitters/${splitter.id}/ports`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [confirmed, splitter]);

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 p-4" data-testid="splitter-connect-dialog">
      <div className="bg-slate-800 border border-slate-700 rounded-lg w-full max-w-md shadow-2xl flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700">
          <h3 className="font-heading font-bold text-base flex items-center gap-2">
            <Boxes size={17} className="text-purple-400" /> {t("Mufa ze spliterem przy trasie")}
          </h3>
          <button onClick={onDecline} data-testid="splitter-connect-close" className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        {!confirmed ? (
          <div className="p-5">
            <p className="text-sm text-slate-300 mb-1">
              {t("Wykryto mufę")} <b className="text-slate-100">{closure?.number || "—"}</b> {t("ze spliterem")} <b className="text-amber-300">{splitter?.number} ({splitter?.split_type})</b>.
            </p>
            <p className="text-sm text-slate-400 mb-5">
              {t("Podłączyć kabel klienta")} {clientName ? <b className="text-slate-200">{clientName}</b> : null} {t("do tego splittera?")}
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={onDecline} data-testid="splitter-decline" className="px-4 py-2 rounded text-sm bg-slate-700 hover:bg-slate-600">{t("Nie, to nie ta — rysuj dalej")}</button>
              <button onClick={() => setConfirmed(true)} data-testid="splitter-confirm" className="px-4 py-2 rounded text-sm bg-cyan-600 hover:bg-cyan-500 text-white font-medium">{t("Tak, wybierz port")}</button>
            </div>
          </div>
        ) : (
          <div className="p-5 overflow-y-auto">
            {loading || !data ? (
              <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 size={20} className="animate-spin mr-2" /> {t("Ładowanie portów…")}</div>
            ) : (
              <>
                <div className="flex items-center gap-3 text-xs mb-3">
                  <span className="text-green-400">{t("Wolne:")} <b>{data.free}</b></span>
                  <span className="text-red-400">{t("Zajęte:")} <b>{data.occupied}</b></span>
                  {data.reserved ? <span className="text-amber-400">{t("Zarezerwowane:")} <b>{data.reserved}</b></span> : null}
                </div>
                <div className="grid grid-cols-2 gap-2" data-testid="splitter-connect-ports">
                  {data.ports.map((p) => {
                    const free = !p.occupied && !p.reserved;
                    const label = p.occupied
                      ? (p.client?.name || (p.splitters?.length ? t("kaskada") : t("zajęty")))
                      : p.reserved ? (p.reserved_for || t("rezerwacja")) : t("wolny");
                    return (
                      <button key={p.port} disabled={!free}
                        onClick={() => free && onConnect(p.port)}
                        data-testid={`connect-port-${p.port}`}
                        title={p.occupied ? (p.client?.name || t("Zajęty")) : p.reserved ? `${t("Zarezerwowane:")} ${p.reserved_for || ""}` : t("Kliknij, aby podłączyć")}
                        className={`text-left rounded-md border px-2.5 py-2 transition-colors ${p.occupied ? "bg-red-900/25 border-red-700/50 cursor-not-allowed" : p.reserved ? "bg-amber-900/20 border-amber-600/50 cursor-not-allowed" : "bg-green-900/15 border-green-700/40 hover:bg-green-900/40 ring-1 ring-green-600/40"}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-mono text-slate-300">{t("Port")} {p.port}</span>
                          <span className={`w-2 h-2 rounded-full ${p.occupied ? "bg-red-400" : p.reserved ? "bg-amber-400" : "bg-green-400"}`} />
                        </div>
                        <div className="text-[11px] truncate mt-0.5 text-slate-400">{label}</div>
                      </button>
                    );
                  })}
                </div>
                <button onClick={onDecline} className="mt-4 w-full text-xs text-slate-400 hover:text-white underline" data-testid="splitter-connect-back">{t("Anuluj — to nie ta mufa, rysuj dalej")}</button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
